import * as React from "react";
import * as ToggleGroupPrimitive from "@radix-ui/react-toggle-group";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

const ToggleGroupContext = React.createContext<{
  track?: "default" | "clay";
  size?: "default" | "sm" | "icon" | "touchPill";
}>({});

const toggleGroupRootVariants = cva("inline-flex items-center", {
  variants: {
    track: {
      default: "gap-1 rounded-lg bg-muted/50 p-1",
      clay: "clay-btn-track h-7 shrink-0 gap-0.5 overflow-hidden rounded-md p-0.5",
    },
  },
  defaultVariants: {
    track: "default",
  },
});

const toggleVariants = cva(
  "inline-flex items-center justify-center gap-1.5 rounded-md text-sm font-medium transition-colors hover:bg-muted hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50 data-[state=on]:bg-accent data-[state=on]:text-accent-foreground [&_svg:not([class*='size-'])]:size-4 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      track: {
        default: "",
        clay:
          "rounded-sm bg-transparent px-2 text-[11px] font-medium shadow-none hover:bg-transparent data-[state=on]:bg-transparent data-[state=on]:text-foreground data-[state=on]:shadow-none",
      },
      size: {
        default: "px-2.5",
        sm: "px-2",
        icon: "",
        touchPill:
          "min-h-11 rounded-full px-3 text-xs md:min-h-9 sm:text-sm",
      },
    },
    compoundVariants: [
      { track: "default", size: "default", class: "h-9" },
      { track: "default", size: "sm", class: "h-8" },
      { track: "default", size: "icon", class: "size-8" },
      { track: "clay", size: "default", class: "h-6" },
      { track: "clay", size: "sm", class: "h-6" },
      { track: "clay", size: "icon", class: "size-6" },
      {
        track: "clay",
        size: "touchPill",
        class: "h-6 min-h-6 rounded-sm px-2 text-[11px]",
      },
    ],
    defaultVariants: {
      track: "default",
      size: "default",
    },
  },
);

const ToggleGroup = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Root> &
    VariantProps<typeof toggleGroupRootVariants> &
    VariantProps<typeof toggleVariants>
>(({ className, track: trackProp = "default", size, children, ...props }, ref) => {
  const track = trackProp ?? "default";
  return (
  <ToggleGroupContext.Provider value={{ track, size: size ?? undefined }}>
    <ToggleGroupPrimitive.Root
      ref={ref}
      className={cn(toggleGroupRootVariants({ track }), className)}
      {...props}
    >
      {React.Children.map(children, (child) =>
        React.isValidElement<{ size?: typeof size }>(child)
          ? React.cloneElement(child, { size: child.props.size ?? size })
          : child,
      )}
    </ToggleGroupPrimitive.Root>
  </ToggleGroupContext.Provider>
  );
});
ToggleGroup.displayName = ToggleGroupPrimitive.Root.displayName;

const ToggleGroupItem = React.forwardRef<
  React.ElementRef<typeof ToggleGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof ToggleGroupPrimitive.Item> &
    VariantProps<typeof toggleVariants>
>(({ className, size, ...props }, ref) => {
  const ctx = React.useContext(ToggleGroupContext);
  const resolvedTrack = ctx.track ?? "default";
  const resolvedSize = size ?? ctx.size ?? "default";

  return (
    <ToggleGroupPrimitive.Item
      ref={ref}
      className={cn(
        toggleVariants({ track: resolvedTrack, size: resolvedSize }),
        className,
      )}
      {...props}
    />
  );
});
ToggleGroupItem.displayName = ToggleGroupPrimitive.Item.displayName;

export { ToggleGroup, ToggleGroupItem };
